import { Flex, Input, Spin, InputProps } from 'antd'
import React, { useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import cn from 'classnames'
import { NumericFormatProps, useNumericFormat, NumberFormatBase } from 'react-number-format'
import { LoadingOutlined } from '@ant-design/icons'
import { usePlaceholder } from './usePlaceholder'
import s from './s.module.css'
import { CurrencyMaskSettings, getCurrencyMaskSettings } from './getCurrencyMaskSettings'
import { getCleanNumber } from './utils'

type OmitNecessaryProps<T> = Omit<T, 'size' | 'suffix' | 'prefix' | 'onChange' | 'type' | 'defaultValue'>
type NumericFormatPropsWithoutIntersect = OmitNecessaryProps<NumericFormatProps>

export interface Props extends Omit<InputProps, 'autoComplete' | 'onChange'>, NumericFormatPropsWithoutIntersect {
  value?: string
  onChange?: (value: string) => void
  onValueChange?: NumericFormatPropsWithoutIntersect['onValueChange']
  currency?: string
  placeholder?: string
  isDisabled?: boolean
  precision?: number
  autoFocus?: boolean
  onFocus?: () => void
  onBlur?: () => void
  dataPointer?: string
  isLoading?: boolean
  spinPosition?: 'left' | 'right'
}

function CurrencyInput({
  value = '',
  onChange,
  currency = '',
  precision,
  placeholder,
  isDisabled,
  autoFocus,
  onFocus,
  onBlur,
  dataPointer,
  isLoading,
  spinPosition = 'right',
  onValueChange,
  ...rest
}: Props) {
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)
  const options: CurrencyMaskSettings = getCurrencyMaskSettings(currency, intl, precision)
  const visiblePlaceholder = usePlaceholder(intl, placeholder, currency, isLoading)

  const handleValueChange = useCallback(
    ({ currentTarget: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      const outputValue = getCleanNumber(value, options.decimalSeparator)

      onChange?.(outputValue)
    },
    [onChange, options.decimalSeparator]
  )

  const numericProps = useNumericFormat({
    thousandsGroupStyle: 'thousand',
    getInputRef: inputRef,
    value: isLoading ? '' : value,
    onChange: handleValueChange,
    onFocus,
    onBlur,
    placeholder: visiblePlaceholder,
    disabled: isDisabled,
    autoFocus,
    valueIsNumericString: true,
    'data-pointer': dataPointer,
    inputMode: 'decimal',
    onValueChange,
    ...options,
    customInput: Input,
  })

  return (
    <Flex className={s.wrapper} align="center">
      {/** eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/** @ts-expect-error: not to cast overlapping props (see OmitNecessaryProps) */}
      <NumberFormatBase {...numericProps} {...rest} />
      <div
        className={cn(s.spin, {
          [s.hidden]: !isLoading,
          [s.right]: spinPosition === 'right',
          [s.left]: spinPosition === 'left',
        })}
      >
        <Spin indicator={<LoadingOutlined style={{ color: 'var(--accent-light)' }} />} size="small" />
      </div>
    </Flex>
  )
}

export default CurrencyInput
